/**
 * @file use-warmup-retry.ts
 * @description Silently retries form submission on dev cold-start failures.
 *              The user must NEVER see a warmup error — the hook keeps the form
 *              in a loading state and retries until the server responds normally.
 * @author William Donaldson
 * @last_modified 2026-02-28
 */

"use client";

import { useEffect, useRef, useState } from "react";

import {
  FORM_ERROR_CODES,
  type FormActionState,
} from "@/server/actions/types";

// WHY 5: Covers even the slowest cold starts (total wait ≈ 2.5s with 500ms
// base delay). In practice, retry #1 or #2 almost always succeeds.
const MAX_RETRIES = 5;

// WHY 500ms: Gives the dev server enough time to finish compiling between
// retries. Too short = wastes retries before the server is ready.
const BASE_DELAY_MS = 500;

/**
 * Silently retries form submission when the server returns a SERVER_WARMUP
 * error. Returns `isRetrying` so the form can keep showing a loading spinner
 * and suppress any error messages during the retry cycle.
 *
 * WHY THIS EXISTS: Next.js 15 dev server loses AsyncLocalStorage scope on
 * the first Server Action call after a cold start. Rather than surfacing this
 * infrastructure quirk to the user, this hook absorbs it completely.
 *
 * Production: inert — the server never sets the SERVER_WARMUP errorCode.
 */
export function useWarmupRetry(
  formRef: React.RefObject<HTMLFormElement | null>,
  state: FormActionState
): { isRetrying: boolean } {
  const retryCount = useRef(0);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    // WHY state AS DEPENDENCY: useActionState returns a new object reference
    // on every action completion. Using the full object (not just errorCode)
    // ensures the effect fires even when consecutive returns have the same
    // errorCode string — which happens during retry sequences.

    if (state.errorCode !== FORM_ERROR_CODES.SERVER_WARMUP) {
      // Success or a real error (wrong password, etc.) — reset everything.
      retryCount.current = 0;
      setIsRetrying(false);
      return;
    }

    // SERVER_WARMUP error — retry if budget remains
    if (retryCount.current >= MAX_RETRIES) {
      // WHY RESET ON EXHAUSTION: Allows the user to click Sign In again
      // and get a fresh retry budget, rather than being permanently stuck.
      retryCount.current = 0;
      setIsRetrying(false);
      return;
    }

    retryCount.current += 1;
    setIsRetrying(true);

    // WHY FIXED DELAY (not increasing): The server usually warms up by
    // retry #1 or #2. A fixed 500ms delay keeps total wait time short
    // while giving the server enough breathing room between attempts.
    const timeoutId = setTimeout(() => {
      formRef.current?.requestSubmit();
    }, BASE_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [state, formRef]);

  return { isRetrying };
}
